import { useEffect, useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Clock, Edit3, PackagePlus, Plus, Tag, Trash2 } from 'lucide-react-native';

import { Badge, Button, Card, EmptyState, SkeletonRows, Sheet, TextField } from '@/components/ui';
import { HistorialStockSheet } from '@/components/stock/HistorialStockSheet';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { productosPetshop, eliminarProductoPetshop, ajustarStockPetshop } from '@/services/firebase/petshops';
import { useAuth, selectIsAdmin } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs } from '@/utils/format';
import { type PetshopId, type PetshopProducto } from '@/types/domain';
import { usePetshopsMeta } from '@/hooks/usePetshopsMeta';

import { ProductoPetshopSheet } from './ProductoPetshopSheet';

interface Props {
  petshop: PetshopId;
}

const COL_STOCK = 80;
const COL_PRECIO = 120;
const COL_ACC = 150;

function ColHead({
  text,
  flex,
  width,
  align = 'left',
  last,
}: {
  text: string;
  flex?: number;
  width?: number;
  align?: 'left' | 'center' | 'right';
  last?: boolean;
}) {
  return (
    <View
      style={{
        flex,
        width,
        paddingHorizontal: align === 'left' ? 18 : align === 'right' ? 14 : 8,
        paddingVertical: 9,
        justifyContent: 'center',
        alignItems: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
        borderRightWidth: last ? 0 : 1,
        borderRightColor: colors.ink[200],
      }}
    >
      <Text style={{ fontSize: 10.5, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 1, textTransform: 'uppercase' }}>
        {text}
      </Text>
    </View>
  );
}

export function CatalogoTab({ petshop }: Props) {
  const isAdmin = useAuth(selectIsAdmin);
  const q = useMemo(() => productosPetshop(petshop), [petshop]);
  const { data, loading } = useFirestoreCollection(q);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PetshopProducto | null>(null);
  const [reponer, setReponer] = useState<PetshopProducto | null>(null);
  const [histProd, setHistProd] = useState<PetshopProducto | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, PetshopProducto[]>();
    data.forEach((p) => {
      const arr = map.get(p.categoria) ?? [];
      arr.push(p);
      map.set(p.categoria, arr);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      <View
        style={{
          paddingHorizontal: 18,
          paddingTop: 16,
          paddingBottom: 12,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Tag size={18} color={colors.magenta[700]} />
          <View>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Catálogo
            </Text>
            <Text style={{ color: colors.ink[500], fontSize: 12 }}>
              {data.length} producto{data.length === 1 ? '' : 's'} · precio y stock
            </Text>
          </View>
        </View>
        {isAdmin ? (
          <Pressable
            onPress={() => {
              setEditing(null);
              setOpen(true);
            }}
            style={({ hovered }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              paddingHorizontal: 14,
              height: 36,
              borderRadius: radius.lg,
              backgroundColor: hovered ? colors.magenta[600] : colors.magenta[700],
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <Plus size={14} color={colors.white} />
            <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 12 }}>Nuevo producto</Text>
          </Pressable>
        ) : null}
      </View>

      {loading ? (
        <View style={{ padding: 16 }}>
          <SkeletonRows rows={3} />
        </View>
      ) : data.length === 0 ? (
        <View style={{ padding: 12 }}>
          <EmptyState
            illustration="shelf"
            title="Catálogo vacío"
            message={
              isAdmin
                ? 'Cargá los productos que vendés de este petshop. Después al registrar una venta, el precio se autocompleta.'
                : 'Pedile a la administradora que cargue el catálogo.'
            }
            actionLabel={isAdmin ? 'Cargar primer producto' : undefined}
            onAction={isAdmin ? () => setOpen(true) : undefined}
          />
        </View>
      ) : (
        <View style={{ borderTopWidth: 1, borderTopColor: colors.ink[100] }}>
          {/* Encabezado de columnas (una sola vez) */}
          <View style={{ flexDirection: 'row', backgroundColor: colors.crema[100], borderBottomWidth: 1, borderBottomColor: colors.ink[200] }}>
            <ColHead text="Producto" flex={1} />
            <ColHead text="Stock" width={COL_STOCK} align="center" />
            <ColHead text="Precio" width={COL_PRECIO} align="right" last={!isAdmin} />
            {isAdmin ? <ColHead text="" width={COL_ACC} align="center" last /> : null}
          </View>

          {grouped.map(([categoria, items]) => (
            <View key={categoria}>
              {/* Banda de categoría (a todo el ancho) */}
              <View
                style={{
                  paddingHorizontal: 18,
                  paddingVertical: 8,
                  backgroundColor: colors.crema[50],
                  borderBottomWidth: 1,
                  borderBottomColor: colors.ink[100],
                  borderTopWidth: 1,
                  borderTopColor: colors.ink[100],
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.magenta[700], letterSpacing: 1.4, textTransform: 'uppercase' }}>
                  {categoria}
                </Text>
                <Badge label={`${items.length}`} tone="neutral" />
              </View>
              {items.map((p, idx) => {
                const lastRow = idx === items.length - 1;
                return (
                  <View
                    key={p.id}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      borderBottomWidth: lastRow ? 0 : 1,
                      borderBottomColor: colors.ink[100],
                    }}
                  >
                    {/* Producto */}
                    <View style={{ flex: 1, paddingHorizontal: 18, paddingVertical: 9, justifyContent: 'center', borderRightWidth: 1, borderRightColor: colors.ink[100] }}>
                      <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }}>{p.nombre}</Text>
                      {p.notas ? (
                        <Text style={{ fontSize: 12, color: colors.ink[500], marginTop: 2 }} numberOfLines={1}>
                          {p.notas}
                        </Text>
                      ) : null}
                    </View>
                    {/* Stock */}
                    <View style={{ width: COL_STOCK, paddingVertical: 9, alignItems: 'center', justifyContent: 'center', borderRightWidth: 1, borderRightColor: colors.ink[100] }}>
                      <Text
                        style={{
                          fontFamily: typography.display.fontFamily,
                          fontSize: 16,
                          fontWeight: typography.weights.bold,
                          color: (p.stock ?? 0) <= 0 ? colors.danger[600] : colors.ink[800],
                        }}
                      >
                        {p.stock ?? 0}
                      </Text>
                    </View>
                    {/* Precio */}
                    <View style={{ width: COL_PRECIO, paddingVertical: 9, paddingHorizontal: 14, alignItems: 'flex-end', justifyContent: 'center', borderRightWidth: isAdmin ? 1 : 0, borderRightColor: colors.ink[100] }}>
                      <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 16, fontWeight: typography.weights.bold, color: colors.success[600] }}>
                        {formatBs(p.precio)}
                      </Text>
                    </View>
                    {/* Acciones */}
                    {isAdmin ? (
                      <View style={{ width: COL_ACC, paddingVertical: 9, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <Pressable
                          onPress={() => setHistProd(p)}
                          hitSlop={6}
                          title="Ver historial de stock"
                          style={({ hovered }) => ({
                            width: 30,
                            height: 30,
                            borderRadius: 9,
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: hovered ? colors.magenta[50] : 'transparent',
                            // @ts-ignore web only
                            cursor: 'pointer',
                          })}
                        >
                          <Clock size={15} color={colors.magenta[700]} />
                        </Pressable>
                        <Pressable
                          onPress={() => setReponer(p)}
                          hitSlop={6}
                          title="Reponer stock"
                          style={({ hovered }) => ({
                            width: 30,
                            height: 30,
                            borderRadius: 9,
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: hovered ? colors.success[50] : 'transparent',
                            // @ts-ignore web only
                            cursor: 'pointer',
                          })}
                        >
                          <PackagePlus size={15} color={colors.success[600]} />
                        </Pressable>
                        <Pressable
                          onPress={() => {
                            setEditing(p);
                            setOpen(true);
                          }}
                          hitSlop={6}
                          style={({ hovered }) => ({
                            width: 30,
                            height: 30,
                            borderRadius: 9,
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: hovered ? colors.ink[100] : 'transparent',
                            // @ts-ignore web only
                            cursor: 'pointer',
                          })}
                        >
                          <Edit3 size={14} color={colors.ink[600]} />
                        </Pressable>
                        <Pressable
                          onPress={() => eliminarProductoPetshop(p.id)}
                          hitSlop={6}
                          style={({ hovered }) => ({
                            width: 30,
                            height: 30,
                            borderRadius: 9,
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: hovered ? colors.danger[50] : 'transparent',
                            // @ts-ignore web only
                            cursor: 'pointer',
                          })}
                        >
                          <Trash2 size={14} color={colors.danger[600]} />
                        </Pressable>
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      )}

      <ProductoPetshopSheet visible={open} onClose={() => setOpen(false)} petshop={petshop} producto={editing} />
      <ReponerSheet visible={!!reponer} onClose={() => setReponer(null)} producto={reponer} petshop={petshop} />
      <HistorialStockSheet visible={!!histProd} onClose={() => setHistProd(null)} productoId={histProd?.id ?? null} productoNombre={histProd?.nombre ?? ''} />
    </Card>
  );
}

// ============================================================================
// Reponer stock de un producto del petshop (agrega N al stock actual)
// ============================================================================
function ReponerSheet({
  visible,
  onClose,
  producto,
  petshop,
}: {
  visible: boolean;
  onClose: () => void;
  producto: PetshopProducto | null;
  petshop: PetshopId;
}) {
  const { metaDe } = usePetshopsMeta();
  const [agregar, setAgregar] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setAgregar('');
    setError(null);
    setSaving(false);
  }, [visible]);

  if (!producto) return null;

  const actual = producto.stock ?? 0;
  const n = Number(agregar.replace(',', '.'));
  const valido = agregar.trim() !== '' && !Number.isNaN(n) && n > 0;
  const queda = actual + (valido ? n : 0);

  const onConfirmar = async () => {
    if (!valido) {
      setError('Poné cuántas unidades agregás.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await ajustarStockPetshop(producto.id, actual + n);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo reponer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="sm"
      title="Reponer stock"
      subtitle={`${producto.nombre} · ${metaDe(petshop).nombre}`}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} disabled={saving} />
          <Button label="Reponer" onPress={onConfirmar} loading={saving} />
        </>
      }
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, padding: 14, borderRadius: radius['2xl'], backgroundColor: colors.crema[50] }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Stock actual
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 26, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            {actual}
          </Text>
        </View>
        <Text style={{ fontSize: 22, color: colors.ink[400] }}>→</Text>
        <View style={{ flex: 1, alignItems: 'flex-end' }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Quedará
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 26, fontWeight: typography.weights.bold, color: colors.success[600] }}>
            {queda}
          </Text>
        </View>
      </View>

      <TextField label="Agregar unidades" value={agregar} onChangeText={setAgregar} keyboardType="number-pad" placeholder="Ej: 12" />

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
